import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { EMOJIS, COLORS, defaultTournamentDates } from "@/lib/helpers";
import { toast } from "sonner";
import { Fish, Plus, X, ChevronRight, ChevronLeft } from "lucide-react";

const STEPS = ["welcome", "comp", "pin", "members", "dates", "done"];

export default function Setup() {
  const { refresh, markUnlocked, setActiveUserId } = useApp();
  const defaults = defaultTournamentDates();
  const [step, setStep] = useState(0);
  const [compName, setCompName] = useState("Glenlyon Fishing Challenge");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [members, setMembers] = useState([]);
  const [start, setStart] = useState(defaults.start);
  const [end, setEnd] = useState(defaults.end);
  const [saving, setSaving] = useState(false);

  // Member draft
  const [draftName, setDraftName] = useState("");
  const [draftEmoji, setDraftEmoji] = useState("🎣");
  const [draftColor, setDraftColor] = useState(COLORS[0]);

  const addDraftMember = () => {
    const name = draftName.trim();
    if (!name) {
      toast.error("Give 'em a name.");
      return;
    }
    setMembers([...members, { name, emoji: draftEmoji, color: draftColor }]);
    setDraftName("");
    setDraftEmoji(EMOJIS[(Math.floor(Math.random() * EMOJIS.length))]);
    setDraftColor(COLORS[members.length % COLORS.length]);
  };

  const removeMember = (idx) => setMembers(members.filter((_, i) => i !== idx));

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canNext = () => {
    if (STEPS[step] === "welcome") return true;
    if (STEPS[step] === "comp") return compName.trim().length > 0;
    if (STEPS[step] === "pin") return pin.length === 4 && pin === pinConfirm;
    if (STEPS[step] === "members") return members.length >= 2;
    if (STEPS[step] === "dates") return start && end && start <= end;
    return true;
  };

  const submit = async () => {
    setSaving(true);
    try {
      const res = await api.createFamily({
        comp_name: compName,
        pin,
        tournament_start: start,
        tournament_end: end,
        members,
      });
      await refresh();
      if (res.members?.[0]) setActiveUserId(res.members[0].id);
      markUnlocked();
      toast.success("Comp's live. Get the rods.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Setup failed. Try again.");
      setSaving(false);
    }
  };

  const current = STEPS[step];

  return (
    <div className="wc-shell flex flex-col min-h-screen bg-[#FAF8F3]">
      {/* Progress bar */}
      <div className="h-1 bg-[#0A3D62]/10">
        <div
          className="h-full bg-[#E8A838] transition-all"
          style={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
        />
      </div>

      <div className="flex-1 px-6 pt-8 pb-32 overflow-y-auto">
        {current === "welcome" && (
          <div className="text-center pt-4" data-testid="setup-welcome-screen">
            <div className="text-7xl mb-6">🎣</div>
            <h1 className="font-display text-4xl sm:text-5xl font-black text-[#0A3D62] leading-[1.05] tracking-tight mb-4">
              Glenlyon Fishing Challenge
            </h1>
            <p className="text-lg text-[#556B7D] leading-relaxed mb-2">
              30-plus years of family fishing.
            </p>
            <p className="text-base text-[#556B7D] leading-relaxed">
              Let's set the scorecard up before someone hooks a monster and starts arguing.
            </p>
          </div>
        )}

        {current === "comp" && (
          <div>
            <h2 className="font-display text-3xl font-black text-[#0A3D62] mb-2">Name the comp</h2>
            <p className="text-[#556B7D] mb-8">Change it if you feel bold. Most don't.</p>
            <input
              data-testid="setup-comp-name-input"
              value={compName}
              onChange={(e) => setCompName(e.target.value)}
              className="w-full text-2xl font-display font-bold text-[#0A3D62] bg-white border-2 border-[#0A3D62]/10 rounded-2xl px-5 py-4 focus:outline-none focus:border-[#E8A838]"
              placeholder="Glenlyon Fishing Challenge"
            />
          </div>
        )}

        {current === "pin" && (
          <div>
            <h2 className="font-display text-3xl font-black text-[#0A3D62] mb-2">
              Set a family PIN
            </h2>
            <p className="text-[#556B7D] mb-8">
              4 digits. Everyone in the family gets it. No individual logins here — trust system.
            </p>
            <label className="block text-sm font-semibold text-[#556B7D] mb-2">PIN</label>
            <input
              data-testid="setup-pin-input"
              type="tel"
              inputMode="numeric"
              maxLength={4}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="w-full text-3xl tracking-[0.6em] font-black text-center text-[#0A3D62] bg-white border-2 border-[#0A3D62]/10 rounded-2xl px-5 py-5 mb-4 focus:outline-none focus:border-[#E8A838]"
              placeholder="••••"
            />
            <label className="block text-sm font-semibold text-[#556B7D] mb-2">Confirm PIN</label>
            <input
              data-testid="setup-pin-confirm-input"
              type="tel"
              inputMode="numeric"
              maxLength={4}
              value={pinConfirm}
              onChange={(e) => setPinConfirm(e.target.value.replace(/\D/g, "").slice(0, 4))}
              className="w-full text-3xl tracking-[0.6em] font-black text-center text-[#0A3D62] bg-white border-2 border-[#0A3D62]/10 rounded-2xl px-5 py-5 focus:outline-none focus:border-[#E8A838]"
              placeholder="••••"
            />
            {pinConfirm && pin !== pinConfirm && (
              <p className="text-[#D9381E] text-sm mt-3 font-medium">PINs don't match.</p>
            )}
          </div>
        )}

        {current === "members" && (
          <div>
            <h2 className="font-display text-3xl font-black text-[#0A3D62] mb-2">
              Add the anglers
            </h2>
            <p className="text-[#556B7D] mb-6">
              Minimum 2. Add everyone — grandkids, cousins, that mate who always claims the biggest.
            </p>

            {members.length > 0 && (
              <div className="mb-6 space-y-2" data-testid="setup-member-list">
                {members.map((m, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-3 bg-white border border-[#0A3D62]/10 rounded-2xl p-3"
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center text-2xl"
                      style={{ background: m.color + "33" }}
                    >
                      {m.emoji}
                    </div>
                    <div className="flex-1 font-semibold text-[#0A3D62]">{m.name}</div>
                    <button
                      data-testid={`setup-remove-member-${idx}`}
                      onClick={() => removeMember(idx)}
                      className="w-9 h-9 rounded-full bg-[#D9381E]/10 text-[#D9381E] flex items-center justify-center active:scale-95"
                      aria-label="Remove"
                    >
                      <X className="w-4 h-4" strokeWidth={2.5} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-white border border-[#0A3D62]/10 rounded-3xl p-5 space-y-4">
              <input
                data-testid="setup-member-name-input"
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addDraftMember()}
                placeholder="Angler's name"
                className="w-full text-xl font-semibold text-[#0A3D62] bg-[#FAF8F3] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#E8A838]"
              />

              <div>
                <div className="text-xs font-bold uppercase text-[#556B7D] mb-2">Avatar</div>
                <div className="flex flex-wrap gap-2">
                  {EMOJIS.map((e) => (
                    <button
                      key={e}
                      data-testid={`setup-emoji-${e}`}
                      onClick={() => setDraftEmoji(e)}
                      className={`w-11 h-11 rounded-xl text-2xl flex items-center justify-center transition-transform active:scale-90 ${
                        draftEmoji === e ? "bg-[#E8A838] scale-110" : "bg-[#FAF8F3]"
                      }`}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs font-bold uppercase text-[#556B7D] mb-2">Colour</div>
                <div className="flex flex-wrap gap-2">
                  {COLORS.map((c) => (
                    <button
                      key={c}
                      data-testid={`setup-color-${c}`}
                      onClick={() => setDraftColor(c)}
                      className="w-9 h-9 rounded-full active:scale-90 transition-transform"
                      style={{
                        background: c,
                        outline: draftColor === c ? "3px solid #1A2A35" : "none",
                        outlineOffset: "2px",
                      }}
                      aria-label={c}
                    />
                  ))}
                </div>
              </div>

              <button
                data-testid="setup-add-member-btn"
                onClick={addDraftMember}
                className="w-full bg-[#0A3D62] text-[#FAF8F3] font-bold py-4 rounded-2xl active:scale-[0.98] transition-transform flex items-center justify-center gap-2"
              >
                <Plus className="w-5 h-5" strokeWidth={2.5} /> Add angler
              </button>
            </div>

            {members.length < 2 && (
              <p className="text-sm text-[#556B7D] mt-4 text-center">
                {members.length === 0
                  ? "Need at least 2 anglers to start."
                  : "One more angler required."}
              </p>
            )}
          </div>
        )}

        {current === "dates" && (
          <div>
            <h2 className="font-display text-3xl font-black text-[#0A3D62] mb-2">
              Tournament dates
            </h2>
            <p className="text-[#556B7D] mb-8">
              We've pre-filled the next long weekend. Change to Easter Fri–Mon if that's the plan.
            </p>
            <label className="block text-sm font-bold uppercase text-[#556B7D] mb-2">Start</label>
            <input
              data-testid="setup-start-date"
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="w-full text-lg font-semibold text-[#0A3D62] bg-white border-2 border-[#0A3D62]/10 rounded-2xl px-4 py-4 mb-5 focus:outline-none focus:border-[#E8A838]"
            />
            <label className="block text-sm font-bold uppercase text-[#556B7D] mb-2">End</label>
            <input
              data-testid="setup-end-date"
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="w-full text-lg font-semibold text-[#0A3D62] bg-white border-2 border-[#0A3D62]/10 rounded-2xl px-4 py-4 focus:outline-none focus:border-[#E8A838]"
            />
          </div>
        )}

        {current === "done" && (
          <div className="text-center pt-8" data-testid="setup-done-screen">
            <Fish className="w-20 h-20 mx-auto text-[#E8A838]" strokeWidth={2.5} />
            <h2 className="font-display text-4xl font-black text-[#0A3D62] mt-6 mb-3">Ready.</h2>
            <p className="text-lg text-[#556B7D] mb-2">{compName} is set.</p>
            <p className="text-base text-[#556B7D]">{members.length} anglers on the water.</p>
          </div>
        )}
      </div>

      {/* Nav footer */}
      <div className="fixed bottom-0 left-0 right-0 pointer-events-none">
        <div className="max-w-lg mx-auto px-6 pb-6 pointer-events-auto bg-gradient-to-t from-[#FAF8F3] via-[#FAF8F3] to-transparent pt-8">
          <div className="flex gap-3">
            {step > 0 && (
              <button
                data-testid="setup-back-btn"
                onClick={back}
                className="w-14 h-14 rounded-2xl bg-white border border-[#0A3D62]/10 text-[#0A3D62] flex items-center justify-center active:scale-95"
              >
                <ChevronLeft className="w-6 h-6" strokeWidth={2.5} />
              </button>
            )}
            {current !== "done" ? (
              <button
                data-testid="setup-next-btn"
                onClick={next}
                disabled={!canNext()}
                className="flex-1 h-14 rounded-2xl bg-[#0A3D62] text-[#FAF8F3] font-bold text-lg flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-40 disabled:active:scale-100"
              >
                {step === STEPS.length - 2 ? "Review" : "Next"}
                <ChevronRight className="w-5 h-5" strokeWidth={2.5} />
              </button>
            ) : (
              <button
                data-testid="setup-finish-btn"
                onClick={submit}
                disabled={saving}
                className="flex-1 h-14 rounded-2xl bg-[#E8A838] text-[#1A2A35] font-black text-lg flex items-center justify-center gap-2 active:scale-[0.98] disabled:opacity-60"
              >
                {saving ? "Launching…" : "Cast off 🎣"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
